import React, { useState, useMemo } from "react";
import {
  X,
  Trash2,
  Plus,
  Edit3,
  ShoppingBag,
  Package,
  User,
  MapPin,
  Phone,
  Mail,
  AlertTriangle,
  Check,
  Search,
  Boxes,
  IndianRupee,
  Receipt,
  FileText
} from "lucide-react";
import { toast } from "sonner";
import { updateOrder, deleteOrder } from "../lib/dataStore";
import {
  recalculateOrderFinancials,
  INDIAN_STATES,
  isWestBengalState,
  calculateItemTax,
  calculateCodCharge,
  orderGrandTotal,
  orderBalanceDue
} from "../lib/orderHelpers";

/**
 * Modal to Edit, Delete, and Add product items to an existing order.
 */
export function EditOrderItemsModal({
  order,
  products = [],
  masterSkus = [],
  isOpen = true,
  onClose,
  onSaved,
}) {
  const [items, setItems] = useState(() => (order?.items || []).map((it) => ({ ...it })));
  const [isAdding, setIsAdding] = useState(false);
  const [addMode, setAddMode] = useState("catalog"); // "catalog" | "sku" | "manual"
  const [isSaving, setIsSaving] = useState(false);

  // Sync items when order prop changes
  React.useEffect(() => {
    if (order?.items) {
      setItems(order.items.map((it) => ({ ...it })));
    }
  }, [order]);

  // Catalog item fields
  const [selectedProductId, setSelectedProductId] = useState(products[0]?.id || "");
  const selectedProduct = useMemo(
    () => products.find((p) => p.id === selectedProductId) || products[0] || null,
    [products, selectedProductId]
  );
  const [selectedVariantTag, setSelectedVariantTag] = useState(
    selectedProduct?.variants?.[0]?.tag || "Default"
  );
  const [catalogQty, setCatalogQty] = useState(1);
  const [catalogPrice, setCatalogPrice] = useState(selectedProduct?.price || 0);
  const [catalogTaxRate, setCatalogTaxRate] = useState(String(selectedProduct?.gstRate || "5"));

  // SKU item fields
  const [skuSearch, setSkuSearch] = useState("");
  const filteredSkus = useMemo(() => {
    if (!skuSearch.trim()) return masterSkus;
    const q = skuSearch.toLowerCase();
    return masterSkus.filter(
      (s) =>
        (s.sku || "").toLowerCase().includes(q) ||
        (s.name || "").toLowerCase().includes(q) ||
        (s.category || "").toLowerCase().includes(q)
    );
  }, [masterSkus, skuSearch]);
  const [selectedSkuId, setSelectedSkuId] = useState(masterSkus[0]?.id || "");
  const selectedSku = useMemo(
    () => masterSkus.find((s) => s.id === selectedSkuId) || filteredSkus[0] || null,
    [masterSkus, selectedSkuId, filteredSkus]
  );
  const [skuQty, setSkuQty] = useState(1);
  const [skuPrice, setSkuPrice] = useState(selectedSku?.saleCost || 0);
  const [skuTaxRate, setSkuTaxRate] = useState(String(selectedSku?.gstRate ?? "5"));

  // Manual item fields
  const [manualName, setManualName] = useState("");
  const [manualVariant, setManualVariant] = useState("Custom");
  const [manualPrice, setManualPrice] = useState("");
  const [manualQty, setManualQty] = useState(1);
  const [manualTaxRate, setManualTaxRate] = useState("5");

  // Keep price updated when product changes
  React.useEffect(() => {
    if (selectedProduct) {
      const v = (selectedProduct.variants || []).find((x) => x.tag === selectedVariantTag) || selectedProduct.variants?.[0];
      setSelectedVariantTag(v?.tag || "Default");
      setCatalogPrice(v?.price != null && v?.price !== "" ? Number(v.price) : Number(selectedProduct.price || 0));
      setCatalogTaxRate(String(selectedProduct.gstRate || "5"));
    }
  }, [selectedProductId, selectedProduct, selectedVariantTag]);

  // Keep price updated when SKU changes
  React.useEffect(() => {
    if (selectedSku) {
      setSkuPrice(Number(selectedSku.saleCost || 0));
      setSkuTaxRate(String(selectedSku.gstRate ?? "5"));
    }
  }, [selectedSku]);

  if (isOpen === false || !order) return null;

  // Real-time preview of totals with current draft items
  const isGst =
    order.isGst !== false &&
    order.isNonGst !== true &&
    order.nonGst !== true &&
    order.gstType !== "non-gst" &&
    order.gstMode !== "none";
  const preview = recalculateOrderFinancials(order, items, products);

  // Edit item quantity
  const handleUpdateQty = (index, delta) => {
    const copy = [...items];
    const newQty = Math.max(1, Number(copy[index].quantity || 1) + delta);
    copy[index].quantity = newQty;
    setItems(copy);
  };

  // Direct quantity input change
  const handleQtyInput = (index, val) => {
    const copy = [...items];
    copy[index].quantity = Math.max(1, Number(val) || 1);
    setItems(copy);
  };

  // Edit item price
  const handlePriceChange = (index, val) => {
    const copy = [...items];
    copy[index].price = Math.max(0, Number(val) || 0);
    setItems(copy);
  };

  // Edit item tax rate
  const handleTaxRateChange = (index, val) => {
    const copy = [...items];
    copy[index].taxRate = Math.max(0, Number(val) || 0);
    copy[index].gstRate = Math.max(0, Number(val) || 0);
    setItems(copy);
  };

  // Delete item from order
  const handleDeleteItem = (index) => {
    if (items.length <= 1) {
      toast.error("An order must have at least 1 item. If the customer cancelled, change order status to Cancelled or delete the order.");
      return;
    }
    const removedItemName = items[index].name;
    const copy = items.filter((_, idx) => idx !== index);
    setItems(copy);
    toast.info(`Removed "${removedItemName}" from order items.`);
  };

  // Add catalog product to items
  const handleAddCatalogItem = () => {
    if (!selectedProduct) return toast.error("Select a product first");
    const numPrice = Math.max(0, Number(catalogPrice || 0));
    const numQty = Math.max(1, Number(catalogQty || 1));
    const numRate = isGst ? Math.max(0, Number(catalogTaxRate || 0)) : 0;

    const newItem = {
      id: selectedProduct.id,
      name: selectedProduct.name,
      variantTag: selectedVariantTag || "Default",
      price: numPrice,
      quantity: numQty,
      taxRate: numRate,
      gstRate: numRate,
      hsn: selectedProduct.hsn || selectedProduct.hsnCode || "4202",
      hsnCode: selectedProduct.hsn || selectedProduct.hsnCode || "4202",
      shippingCharge: Number(selectedProduct.shippingCharge || 0),
      isManual: false,
    };

    setItems([...items, newItem]);
    setIsAdding(false);
    toast.success(`Added ${selectedProduct.name} (${selectedVariantTag}) to order`);
  };

  // Add SKU to items
  const handleAddSkuItem = () => {
    if (!selectedSku) return toast.error("Select a Master SKU first");
    const numPrice = Math.max(0, Number(skuPrice || 0));
    const numQty = Math.max(1, Number(skuQty || 1));
    const numRate = isGst ? Math.max(0, Number(skuTaxRate || 0)) : 0;

    const newItem = {
      id: selectedSku.id,
      sku: selectedSku.sku,
      name: selectedSku.name,
      variantTag: selectedSku.sku,
      price: numPrice,
      quantity: numQty,
      taxRate: numRate,
      gstRate: numRate,
      hsn: selectedSku.hsn || selectedSku.hsnCode || "4202",
      hsnCode: selectedSku.hsn || selectedSku.hsnCode || "4202",
      purchaseCost: Number(selectedSku.purchaseCost || 0),
      additionalCost: Number(selectedSku.additionalCost || 0),
      shippingCharge: Number(selectedSku.shippingCharge || 0),
      isMasterSku: true,
    };

    setItems([...items, newItem]);
    setIsAdding(false);
    toast.success(`Added SKU ${selectedSku.sku} to order`);
  };

  // Add manual item
  const handleAddManualItem = () => {
    if (!manualName.trim()) return toast.error("Please enter item name");
    const numPrice = Math.max(0, Number(manualPrice || 0));
    const numQty = Math.max(1, Number(manualQty || 1));
    const numRate = isGst ? Math.max(0, Number(manualTaxRate || 0)) : 0;

    const newItem = {
      id: `manual_${Date.now()}`,
      name: manualName.trim(),
      variantTag: manualVariant.trim() || "Custom",
      price: numPrice,
      quantity: numQty,
      taxRate: numRate,
      gstRate: numRate,
      hsn: "4202",
      hsnCode: "4202",
      isManual: true,
    };

    setItems([...items, newItem]);
    setManualName("");
    setManualPrice("");
    setIsAdding(false);
    toast.success(`Added custom item "${newItem.name}" to order`);
  };

  // Save all items to order
  const handleSave = async () => {
    if (items.length === 0) {
      return toast.error("Order must contain at least one product item.");
    }

    setIsSaving(true);
    try {
      const financials = recalculateOrderFinancials(order, items, products);
      await updateOrder(order.id, financials);
      toast.success("Order items and financial totals updated successfully!");
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error("Failed to update order items:", err);
      toast.error(err.message || "Failed to save order items");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-2xl w-full max-w-3xl overflow-hidden shadow-2xl flex flex-col max-h-[92vh] animate-in fade-in-50 zoom-in-95">
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-border flex items-center justify-between bg-muted/30">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-terracotta/10 text-terracotta">
                <ShoppingBag className="w-4 h-4" />
              </span>
              <h2 className="text-base sm:text-lg font-bold font-serif text-foreground">
                Edit &amp; Manage Order Items
              </h2>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Order ID: <span className="font-mono font-semibold text-foreground uppercase">{order.id}</span> • Customer: {order.customerName || order.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition cursor-pointer"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-4 flex-1">
          {/* Current Items List */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Order Items ({items.length})
              </span>
              {!isAdding && (
                <button
                  type="button"
                  data-testid="admin-add-item-btn"
                  onClick={() => setIsAdding(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-terracotta text-primary-foreground text-xs font-semibold hover:opacity-90 transition cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" /> Add Product to Order
                </button>
              )}
            </div>

            <div className="border border-border rounded-xl overflow-hidden divide-y divide-border bg-background">
              {items.map((it, idx) => {
                const itemRate = isGst ? Number(it.taxRate != null ? it.taxRate : (it.gstRate || 0)) : 0;
                const itemPrice = Number(it.price || 0);
                const itemQty = Number(it.quantity || 1);
                const lineBase = itemPrice * itemQty;
                const lineTax = (lineBase * itemRate) / 100;
                const lineTotal = lineBase + lineTax;

                return (
                  <div key={idx} className="p-3 sm:p-3.5 hover:bg-muted/20 transition space-y-2 sm:space-y-0 sm:flex sm:items-center sm:justify-between sm:gap-4">
                    {/* Item Details */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-xs sm:text-sm text-foreground flex flex-wrap items-center gap-1.5">
                        <span className="truncate">{it.name}</span>
                        {it.variantTag && it.variantTag !== "Default" && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">
                            {it.variantTag}
                          </span>
                        )}
                        {it.sku && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-foreground border border-border">
                            SKU: {it.sku}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2">
                        <span>HSN: {it.hsn || it.hsnCode || "4202"}</span>
                        <span>• Base: ₹{lineBase.toFixed(2)}</span>
                        {itemRate > 0 && <span>• Tax ({itemRate}%): ₹{lineTax.toFixed(2)}</span>}
                      </div>
                    </div>

                    {/* Inline Item Controls */}
                    <div className="flex flex-wrap items-center gap-3 shrink-0">
                      {/* Price Control */}
                      <label className="flex items-center gap-1 text-xs">
                        <span className="text-[10px] text-muted-foreground">Price ₹</span>
                        <input
                          type="number"
                          min="0"
                          data-testid={`admin-item-price-${idx}`}
                          value={it.price}
                          onChange={(e) => handlePriceChange(idx, e.target.value)}
                          className="w-20 bg-muted/50 border border-border px-2 py-1 rounded text-xs font-mono font-semibold text-foreground text-right"
                        />
                      </label>

                      {/* Quantity Control */}
                      <div className="flex items-center border border-border rounded bg-muted/40 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => handleUpdateQty(idx, -1)}
                          className="px-2 py-1 text-xs font-bold hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min="1"
                          data-testid={`admin-item-qty-${idx}`}
                          value={it.quantity}
                          onChange={(e) => handleQtyInput(idx, e.target.value)}
                          className="w-10 bg-transparent text-center text-xs font-mono font-bold text-foreground border-x border-border/60 py-1"
                        />
                        <button
                          type="button"
                          onClick={() => handleUpdateQty(idx, 1)}
                          className="px-2 py-1 text-xs font-bold hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer"
                        >
                          +
                        </button>
                      </div>

                      {/* GST Rate Control */}
                      {isGst && (
                        <label className="flex items-center gap-1 text-xs">
                          <span className="text-[10px] text-muted-foreground">GST%</span>
                          <select
                            data-testid={`admin-item-gst-${idx}`}
                            value={it.taxRate != null ? it.taxRate : (it.gstRate || 0)}
                            onChange={(e) => handleTaxRateChange(idx, e.target.value)}
                            className="bg-muted/50 border border-border px-1.5 py-1 rounded text-xs font-mono"
                          >
                            <option value="0">0%</option>
                            <option value="5">5%</option>
                            <option value="12">12%</option>
                            <option value="18">18%</option>
                            <option value="28">28%</option>
                          </select>
                        </label>
                      )}

                      {/* Line Total */}
                      <div className="text-right w-20">
                        <span className="text-xs font-bold font-mono font-price text-foreground block">
                          ₹{Math.round(lineTotal)}
                        </span>
                        <span className="text-[9px] text-muted-foreground block">
                          Total
                        </span>
                      </div>

                      {/* Delete Item Button */}
                      <button
                        type="button"
                        data-testid={`admin-delete-item-${idx}`}
                        onClick={() => handleDeleteItem(idx)}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition cursor-pointer"
                        title="Remove item from order"
                      >
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Add Product Item Panel (Expandable) */}
          {isAdding && (
            <div className="bg-card border-2 border-dashed border-terracotta/40 rounded-xl p-4 space-y-3 animate-in fade-in-50">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-terracotta flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" /> Add New Item to Order
                </span>
                <div className="flex items-center gap-1.5 bg-muted p-0.5 rounded-lg text-xs">
                  <button
                    type="button"
                    onClick={() => setAddMode("catalog")}
                    className={`px-2.5 py-1 rounded-md font-medium transition ${addMode === "catalog" ? "bg-background text-foreground shadow-xs font-bold" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    Catalog Product
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddMode("sku")}
                    className={`px-2.5 py-1 rounded-md font-medium transition ${addMode === "sku" ? "bg-background text-foreground shadow-xs font-bold" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    Master SKU
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddMode("manual")}
                    className={`px-2.5 py-1 rounded-md font-medium transition ${addMode === "manual" ? "bg-background text-foreground shadow-xs font-bold" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    Custom Manual Item
                  </button>
                </div>
              </div>

              {/* Mode 1: Catalog Product */}
              {addMode === "catalog" && (
                <div className="grid sm:grid-cols-4 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-[11px] font-medium text-muted-foreground mb-1">
                      Select Product
                    </label>
                    <select
                      value={selectedProductId}
                      onChange={(e) => setSelectedProductId(e.target.value)}
                      className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs font-medium"
                    >
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} {p.sku ? `(${p.sku})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium text-muted-foreground mb-1">
                      Variant
                    </label>
                    <select
                      value={selectedVariantTag}
                      onChange={(e) => setSelectedVariantTag(e.target.value)}
                      className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs font-medium"
                    >
                      {(selectedProduct?.variants || [{ tag: "Default" }]).map((v) => (
                        <option key={v.tag} value={v.tag}>
                          {v.tag} {v.stock != null ? `(${v.stock} in stock)` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex gap-2">
                    <div className="w-1/2">
                      <label className="block text-[11px] font-medium text-muted-foreground mb-1">
                        Price (₹)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={catalogPrice}
                        onChange={(e) => setCatalogPrice(e.target.value)}
                        className="w-full bg-background border border-border px-2 py-1.5 rounded-md text-xs font-mono font-bold"
                      />
                    </div>
                    <div className="w-1/2">
                      <label className="block text-[11px] font-medium text-muted-foreground mb-1">
                        Qty
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={catalogQty}
                        onChange={(e) => setCatalogQty(Math.max(1, Number(e.target.value)))}
                        className="w-full bg-background border border-border px-2 py-1.5 rounded-md text-xs font-mono font-bold text-center"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Mode 2: Master SKU */}
              {addMode === "sku" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 bg-background border border-border px-2.5 py-1 rounded-md">
                    <Search className="w-3.5 h-3.5 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search SKU by code, name, category…"
                      value={skuSearch}
                      onChange={(e) => setSkuSearch(e.target.value)}
                      className="bg-transparent text-xs w-full outline-none"
                    />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <label className="block text-[11px] font-medium text-muted-foreground mb-1">
                        Select Master SKU
                      </label>
                      <select
                        value={selectedSkuId}
                        onChange={(e) => setSelectedSkuId(e.target.value)}
                        className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs font-medium font-mono"
                      >
                        {filteredSkus.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.sku} — {s.name} (Stock: {s.stock ?? 0})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-1/2">
                        <label className="block text-[11px] font-medium text-muted-foreground mb-1">
                          Price (₹)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={skuPrice}
                          onChange={(e) => setSkuPrice(e.target.value)}
                          className="w-full bg-background border border-border px-2 py-1.5 rounded-md text-xs font-mono font-bold"
                        />
                      </div>
                      <div className="w-1/2">
                        <label className="block text-[11px] font-medium text-muted-foreground mb-1">
                          Qty
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={skuQty}
                          onChange={(e) => setSkuQty(Math.max(1, Number(e.target.value)))}
                          className="w-full bg-background border border-border px-2 py-1.5 rounded-md text-xs font-mono font-bold text-center"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Mode 3: Custom Manual Item */}
              {addMode === "manual" && (
                <div className="grid sm:grid-cols-4 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-[11px] font-medium text-muted-foreground mb-1">
                      Item Name *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Custom Monogram / Extra Strap"
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-muted-foreground mb-1">
                      Price (₹) *
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={manualPrice}
                      onChange={(e) => setManualPrice(e.target.value)}
                      className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs font-mono font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-muted-foreground mb-1">
                      Quantity
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={manualQty}
                      onChange={(e) => setManualQty(Math.max(1, Number(e.target.value)))}
                      className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs font-mono font-bold text-center"
                    />
                  </div>
                </div>
              )}

              {/* Action Buttons for Add Item Panel */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/60">
                <button
                  type="button"
                  onClick={() => setIsAdding(false)}
                  className="px-3 py-1 rounded text-xs text-muted-foreground hover:text-foreground cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="admin-confirm-add-item-btn"
                  onClick={
                    addMode === "catalog"
                      ? handleAddCatalogItem
                      : addMode === "sku"
                      ? handleAddSkuItem
                      : handleAddManualItem
                  }
                  className="px-3.5 py-1 rounded bg-terracotta text-primary-foreground text-xs font-semibold hover:opacity-90 transition cursor-pointer"
                >
                  Confirm &amp; Add to List
                </button>
              </div>
            </div>
          )}

          {/* Real-time Order Summary Card */}
          <div className="bg-muted/30 border border-border rounded-xl p-4 space-y-2">
            <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block">
              Financial Summary (Updated in Real-Time)
            </span>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="p-2 rounded bg-card border border-border">
                <span className="text-[10px] text-muted-foreground block">Items Subtotal</span>
                <span className="font-bold text-foreground font-mono text-sm">₹{preview.subtotal}</span>
              </div>
              <div className="p-2 rounded bg-card border border-border">
                <span className="text-[10px] text-muted-foreground block">Calculated Tax</span>
                <span className="font-bold text-emerald-700 dark:text-emerald-400 font-mono text-sm">₹{preview.tax}</span>
              </div>
              <div className="p-2 rounded bg-card border border-border">
                <span className="text-[10px] text-muted-foreground block">Shipping</span>
                <span className="font-bold text-blue-700 dark:text-blue-400 font-mono text-sm">
                  {order.deliveryIncluded === false ? `+₹${order.shippingCharge || 0}` : "Free (Included)"}
                </span>
              </div>
              <div className="p-2 rounded bg-card border border-border">
                <span className="text-[10px] text-muted-foreground block">New Grand Total</span>
                <span className="font-bold text-terracotta font-mono text-base">₹{preview.total}</span>
              </div>
            </div>
            {Number(order.codCharge || 0) > 0 && (
              <div className="text-[11px] text-amber-800 dark:text-amber-300 pt-1 border-t border-border/60 flex items-center justify-between">
                <span>COD Advance: ₹{order.advancePaid || 0}</span>
                <span className="font-bold">New Collect on Delivery: ₹{preview.codBalanceDue}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-5 border-t border-border bg-muted/30 flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            Changes will update inventory and invoice amounts immediately.
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-muted transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="admin-save-items-btn"
              onClick={handleSave}
              disabled={isSaving}
              className="px-5 py-2 rounded-lg bg-terracotta text-primary-foreground text-xs font-semibold hover:opacity-90 transition cursor-pointer flex items-center gap-1.5 shadow-xs"
            >
              {isSaving ? "Saving…" : <><Check className="w-3.5 h-3.5" /> Save Items &amp; Update Order</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Modal to Edit or Delete Customer & Delivery details for an existing order.
 */
export function EditCustomerModal({
  order,
  products = [],
  isOpen = true,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    customerName: order?.customerName || order?.name || "",
    phone: order?.phone || "",
    email: order?.email || "",
    address: order?.address || "",
    city: order?.city || "",
    state: order?.state || "West Bengal",
    pincode: order?.pincode || order?.pin || "",
    customerGst: order?.customerGst || order?.gstin || "",
    notes: order?.notes || "",
  });
  const [isSaving, setIsSaving] = useState(false);

  // Sync initial state if order changes
  React.useEffect(() => {
    if (order) {
      setForm({
        customerName: order.customerName || order.name || "",
        phone: order.phone || "",
        email: order.email || "",
        address: order.address || "",
        city: order.city || "",
        state: order.state || "West Bengal",
        pincode: order.pincode || order.pin || "",
        customerGst: order.customerGst || order.gstin || "",
        notes: order.notes || "",
      });
    }
  }, [order]);

  if (isOpen === false || !order) return null;

  const isWb = isWestBengalState(form.state);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customerName.trim()) return toast.error("Customer name is required");
    if (!form.phone.trim()) return toast.error("Phone number is required");
    if (!form.address.trim()) return toast.error("Address is required");

    setIsSaving(true);
    try {
      // If state changed between WB and non-WB, recalculate CGST/SGST vs IGST
      const stateChanged = form.state !== (order.state || "West Bengal");
      let financials = {};
      if (stateChanged) {
        financials = recalculateOrderFinancials(
          { ...order, state: form.state.trim() },
          order.items,
          products
        );
      }

      const patch = {
        customerName: form.customerName.trim(),
        name: form.customerName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        pincode: form.pincode.trim(),
        pin: form.pincode.trim(),
        customerGst: form.customerGst.trim().toUpperCase(),
        gstin: form.customerGst.trim().toUpperCase(),
        notes: form.notes.trim(),
        isIntraState: isWb,
        breakdownCgstSgst: isWb,
        ...financials,
      };

      await updateOrder(order.id, patch);
      toast.success("Customer details updated successfully!");
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error("Failed to update customer details:", err);
      toast.error(err.message || "Failed to save customer details");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-2xl w-full max-w-xl overflow-hidden shadow-2xl flex flex-col max-h-[92vh] animate-in fade-in-50 zoom-in-95">
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-border flex items-center justify-between bg-muted/30">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-terracotta/10 text-terracotta">
                <User className="w-4 h-4" />
              </span>
              <h2 className="text-base sm:text-lg font-bold font-serif text-foreground">
                Edit Customer &amp; Shipping Details
              </h2>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Order ID: <span className="font-mono font-semibold text-foreground uppercase">{order.id}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition cursor-pointer"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 overflow-y-auto space-y-4 flex-1">
          <div className="grid sm:grid-cols-2 gap-3">
            {/* Customer Name */}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Customer Name *
              </label>
              <div className="flex items-center bg-background border border-border rounded-lg px-2.5 py-1.5 focus-within:border-terracotta">
                <User className="w-4 h-4 text-muted-foreground mr-2 shrink-0" />
                <input
                  type="text"
                  required
                  data-testid="admin-edit-customer-name"
                  value={form.customerName}
                  onChange={(e) => setForm({ ...form, customerName: e.target.value })}
                  className="w-full text-xs bg-transparent outline-none text-foreground font-medium"
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Phone Number *
              </label>
              <div className="flex items-center bg-background border border-border rounded-lg px-2.5 py-1.5 focus-within:border-terracotta">
                <Phone className="w-4 h-4 text-muted-foreground mr-2 shrink-0" />
                <input
                  type="text"
                  required
                  data-testid="admin-edit-customer-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full text-xs bg-transparent outline-none text-foreground font-mono font-medium"
                />
              </div>
            </div>

            {/* Email */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-foreground mb-1">
                Email Address
              </label>
              <div className="flex items-center bg-background border border-border rounded-lg px-2.5 py-1.5 focus-within:border-terracotta">
                <Mail className="w-4 h-4 text-muted-foreground mr-2 shrink-0" />
                <input
                  type="email"
                  data-testid="admin-edit-customer-email"
                  placeholder="customer@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full text-xs bg-transparent outline-none text-foreground font-medium"
                />
              </div>
            </div>

            {/* Address */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-foreground mb-1">
                Shipping Street Address *
              </label>
              <textarea
                required
                rows={2}
                data-testid="admin-edit-customer-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full bg-background border border-border rounded-lg p-2.5 text-xs text-foreground outline-none focus:border-terracotta"
              />
            </div>

            {/* City */}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                City / Town *
              </label>
              <input
                type="text"
                required
                data-testid="admin-edit-customer-city"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full bg-background border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-terracotta font-medium"
              />
            </div>

            {/* State */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-foreground">
                  State *
                </label>
                <span className={`text-[10px] px-1.5 py-0.2 rounded font-bold uppercase ${isWb ? "bg-emerald-500/10 text-emerald-700" : "bg-blue-500/10 text-blue-700"}`}>
                  {isWb ? "CGST + SGST (WB)" : "IGST (Inter-State)"}
                </span>
              </div>
              <select
                data-testid="admin-edit-customer-state"
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
                className="w-full bg-background border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-terracotta font-medium cursor-pointer"
              >
                {INDIAN_STATES.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>

            {/* Pin Code */}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Pin Code *
              </label>
              <input
                type="text"
                required
                data-testid="admin-edit-customer-pincode"
                value={form.pincode}
                onChange={(e) => setForm({ ...form, pincode: e.target.value })}
                className="w-full bg-background border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-terracotta font-mono font-medium"
              />
            </div>

            {/* Customer GSTIN */}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                Customer GSTIN (Optional)
              </label>
              <input
                type="text"
                data-testid="admin-edit-customer-gst"
                placeholder="e.g. 19ABCDE1234F1Z5"
                value={form.customerGst}
                onChange={(e) => setForm({ ...form, customerGst: e.target.value })}
                className="w-full bg-background border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-terracotta font-mono font-medium uppercase"
              />
            </div>

            {/* Order Notes */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-foreground mb-1">
                Order Notes / Special Instructions
              </label>
              <input
                type="text"
                data-testid="admin-edit-customer-notes"
                placeholder="e.g. Call before delivery, gift wrap, urgent"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full bg-background border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-terracotta"
              />
            </div>
          </div>

          <div className="p-3 bg-muted/40 border border-border rounded-xl text-[11px] text-muted-foreground flex items-center justify-between">
            <span>Customer address will update on printable invoice and shipping label instantly.</span>
            {form.customerGst && (
              <button
                type="button"
                onClick={() => setForm({ ...form, customerGst: "" })}
                className="text-terracotta hover:underline cursor-pointer font-medium"
              >
                Clear GSTIN
              </button>
            )}
          </div>

          {/* Footer inside form */}
          <div className="pt-3 border-t border-border flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-muted transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="admin-save-customer-btn"
              disabled={isSaving}
              className="px-5 py-2 rounded-lg bg-terracotta text-primary-foreground text-xs font-semibold hover:opacity-90 transition cursor-pointer flex items-center gap-1.5 shadow-xs"
            >
              {isSaving ? "Saving…" : <><Check className="w-3.5 h-3.5" /> Save Customer Details</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Confirmation dialog to permanently delete an entire order.
 */
export function DeleteOrderConfirmModal({
  order,
  isOpen = true,
  onClose,
  onDeleted,
}) {
  const [isDeleting, setIsDeleting] = useState(false);

  if (isOpen === false || !order) return null;

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteOrder(order.id);
      toast.success(`Order #${String(order.id).toUpperCase()} deleted permanently.`);
      if (onDeleted) onDeleted();
      onClose();
    } catch (err) {
      console.error("Failed to delete order:", err);
      toast.error(err.message || "Failed to delete order");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-card border border-rose-300 dark:border-rose-900 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl p-5 sm:p-6 space-y-4 animate-in fade-in-50 zoom-in-95">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-full bg-rose-500/10 text-rose-600 shrink-0">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-base font-bold font-serif text-foreground">
              Delete Order Permanently?
            </h3>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Are you sure you want to delete order{" "}
              <strong className="font-mono text-foreground font-semibold">
                #{String(order.id).toUpperCase()}
              </strong>{" "}
              for <strong>{order.customerName || order.name || "Customer"}</strong>?
            </p>
          </div>
        </div>

        <div className="p-3 bg-muted/40 border border-border rounded-xl text-xs space-y-1">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Order Total:</span>
            <span className="font-bold font-mono">₹{order.total || 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Items:</span>
            <span>{order.items?.length || 0} product(s)</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Status:</span>
            <span className="font-semibold text-foreground">{order.status}</span>
          </div>
        </div>

        <p className="text-[11px] text-rose-600 dark:text-rose-400 font-medium">
          Warning: This action is irreversible. The order will be removed from sales, financial accounting, and GST reporting.
        </p>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-muted transition cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="admin-confirm-delete-order-btn"
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-4 py-2 rounded-lg bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700 transition cursor-pointer flex items-center gap-1.5 shadow-xs"
          >
            {isDeleting ? "Deleting…" : <><Trash2 className="w-3.5 h-3.5" /> Delete Order</>}
          </button>
        </div>
      </div>
    </div>
  );
}
